/**
 * <DoneActivitiesAccordion> — le bloc « Faites », plié : son intitulé et son compte, « Afficher » /
 * « Masquer », puis les activités faites et « Voir plus · Activités faites ».
 *
 * Le même sur l'onglet Activités (`<ActivitiesSection>`) et dans la feuille des activités du live
 * (`<LiveActivitiesSheet>`).
 *
 * `history` : au lieu de sortir vers un écran, « Voir plus » devient « 24 activités terminées », qui
 * déplie la liste sur place (la fiche d'un élève, où rien ne s'empile ; elle y écrit « Ses 24… »).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivity } from "../../../ports/primary/ui/types"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"
import { Icon } from "../../atoms/icons/Icon"
import { DelayedSkeleton } from "../../atoms/Skeleton"
import { ActivityRowSkeleton } from "../../molecules/ActivityRow/ActivityRowSkeleton"
import { countLabel } from "../../../ports/primary/ui/formatters/textFormatter"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Accordion } from "../../molecules/Accordion"
import { ActivityRow } from "../../molecules/ActivityRow"
import { CardList, CardListLinkRow } from "../../molecules/CardList"

const TEXTS = {
    title: "Faites",
    show: "Afficher",
    hide: "Masquer",
    seeAll: "Voir plus",
    seeAllHint: "· Activités faites",
    history: (count: number) => countLabel(count, "activité terminée", "activités terminées"),
    historyHide: "Masquer les activités terminées",
}

export type DoneActivitiesAccordionProps = {
    activities: UIActivity[]
    /** Toucher une activité faite. Absent : les lignes ne pressent pas. */
    onPress?: (activity: UIActivity) => void
    /** « Voir plus ». Absent : pas de lien. */
    onSeeAll?: () => void
    /** Le bloc s'ouvre déplié. Défaut `false`. */
    defaultExpanded?: boolean
    /** L'intitulé. Défaut « Faites ». */
    title?: string
    /** Toutes ses activités terminées, à déplier sur place. Absent : « Voir plus » (`onSeeAll`). */
    history?: UIDoneActivitiesHistory
}

/** Les activités terminées d'un élève, dépliées dans le bloc. Contrôlé : l'écran les lit à l'ouverture. */
export type UIDoneActivitiesHistory = {
    /** Le total : « 24 activités terminées ». */
    count: number
    /** Le lien, replié, à la voix de l'écran (« Ses 24 activités terminées »). Défaut : « 24 activités terminées ». */
    label?: string
    /** Le lien, déplié. Défaut « Masquer les activités terminées ». */
    hideLabel?: string
    open: boolean
    onToggle: () => void
    /** Les terminées AVANT aujourd'hui : celles du jour sont déjà au-dessus (`activities` du bloc). */
    activities: UIActivity[]
    hasMore?: boolean
    loadingMore?: boolean
    onLoadMore?: () => void
}

export function DoneActivitiesAccordion(props: DoneActivitiesAccordionProps) {
    const { activities, onPress, onSeeAll, defaultExpanded, title = TEXTS.title, history } = props
    return (
        <Accordion
            defaultExpanded={defaultExpanded}
            header={(expanded) => (
                <Row justify="between" align="center">
                    <Row gap="2" align="center">
                        <SectionLabel>{title}</SectionLabel>
                        <Badge text={String(activities.length)} variant="neutral" tinted />
                    </Row>
                    <Row gap="1" align="center">
                        <Text size="text-md" weight="bold" color="primary">
                            {expanded ? TEXTS.hide : TEXTS.show}
                        </Text>
                        <Icon name={expanded ? "chevron-up" : "chevron-down"} size={16} color="primary" />
                    </Row>
                </Row>
            )}
        >
            <View style={styles.body}>
                <CardList>
                    {(history?.open === true ? [...activities, ...history.activities] : activities).map((activity) => (
                        <ActivityRow key={activity.id} activity={activity} onPress={onPress} />
                    ))}
                    {history == null && onSeeAll != null && (
                        <CardListLinkRow label={TEXTS.seeAll} hint={TEXTS.seeAllHint} onPress={onSeeAll} />
                    )}
                    {history?.open === true &&
                        (history.loadingMore === true ? (
                            // La suite arrive : deux lignes de sa forme, comme au pied de « Activités faites ».
                            <DelayedSkeleton>
                                <ActivityRowSkeleton titleWidth={160} done />
                                <ActivityRowSkeleton titleWidth={120} done divided />
                            </DelayedSkeleton>
                        ) : history.hasMore === true && history.onLoadMore != null ? (
                            <CardListLinkRow label={TEXTS.seeAll} onPress={history.onLoadMore} />
                        ) : null)}
                    {history != null && (
                        <CardListLinkRow
                            label={
                                history.open
                                    ? (history.hideLabel ?? TEXTS.historyHide)
                                    : (history.label ?? TEXTS.history(history.count))
                            }
                            onPress={history.onToggle}
                        />
                    )}
                </CardList>
            </View>
        </Accordion>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** Le pli : la carte se décolle de son intitulé, comme les autres sections. */
    body: {
        paddingTop: theme.spacing["3"],
    },
}))
